<template src="./template.html"></template>
<style src="./styles.scss" lang="scss" scoped></style>

<script>
import {
  fixEditorContentForLineClamp,
  getSocialMediaIconForSource
} from '@/helper'
import { mapActions, mapState } from 'vuex'
import { isObjectLike, some } from 'lodash'
import api from '@/api'
import { isMobile } from 'mobile-device-detect'
import AttachmentItem from './AttachmentItem'
import LinkPreviewItem from './LinkPreviewItem'
import EditPostOverlay from '@/components/overlays/EditPostOverlay'
import FallbackAvatar from '@/components/ui/FallbackAvatar'
import EventPreview from '@/components/layout/EventPreview'
import ConfirmationOverlay from '@/components/overlays/ConfirmationOverlay'
import OverlayGallery from '@/components/overlays/OverlayGallery'
import NewsItemOverlay from '@/components/overlays/NewsItemOverlay'
import LumaCalculator from '@/components/helper/LumaCalculator'

export default {
  name: 'NewsItem',
  components: {
    AttachmentItem,
    LinkPreviewItem,
    EditPostOverlay,
    ConfirmationOverlay,
    CTA: () => import('@/components/modules/CTA'),
    OverlayGallery,
    EventPreview,
    FallbackAvatar,
    LumaCalculator,
    NewsItemOverlay,
  },
  props: {
    news: { type: Object, default: null },
    showActionItems: Boolean,
    groupId: { type: Number, default: null },
    topicImg: String,
    topicId: { type: Number, default: null },
    hash: String,
  },
  data() {
    return {
      overlayIndex: null,
      attachments: [],
      selectedOverlay: null,
      breadcrumbItems: [],
      clampLines: 0,
      isClamped: false,
      initialClamped: false,
      comments: {
        data: {},
      },
      likes: {
        data: {},
      },
      statusMessage: null,
      isStatusVisible: false,
      currentIndex: 0,
      luma: null,
      imageSource: null,
      indicators: 'bright',
      selectedArea: null,
    }
  },
  computed: {
    ...mapState('auth', ['settings']),
    canDelete() {
      return this.news && some(this.news.permissions, p => p === 'delete')
    },
    canEdit() {
      return this.news && some(this.news.permissions, p => p === 'edit')
    },
    getLineClampLines() {
      if (this.isClamped) {
        return 0
      } else if (this.exceptionalCustomers()) {
        const base_lines = this.attachments.length ? 8 : 7
        if (this.news.meta.type === 'article') {
          return 8
        }
        return this.showTitle ? base_lines - 4 : base_lines
      } else {
        return isMobile ? 7 : 4
      }
    },
    getDeletePostObj() {
      const delete_post_object = this.$t('news').news_item.overlays.delete_post
      delete_post_object['img'] = delete_post_object['img'] || this.topicImg
      return delete_post_object
    },
    showTitle() {
      const { title, teaser } = this.news
      return title &&
             teaser.toLowerCase().indexOf(title.toLowerCase().slice(0, -4)) ===
             -1
    },
    toggleText() {
      return this.isClamped ? this.$t('toggle_show.expand') : this.$t(
        'toggle_show.collapse')
    },
    toggleIcon() {
      return this.isClamped ? 'mdi-chevron-down' : 'mdi-chevron-up'
    },
  },
  watch: {
    currentIndex: {
      immediate: true,
      handler(val) {
        this.onChangeSlide(val)
      },
    },
    luma: {
      immediate: true,
      handler(val) {
        this.indicators = val !== null && val > 155 ? 'dark' : 'bright'
      },
    },
  },
  created() {
    const meta = this.news.meta
    const breadcrumbItems = []
    if (!this.groupId && meta.group && meta.type !== 'article') {
      breadcrumbItems.push({
        exact: true,
        text: meta.group.name,
        to: {
          name: 'group',
          params: { id: meta.group.id },
        },
        disabled: false,
      })
    }
    if (!this.topicId && meta.topic) {
      if (meta.group && meta.type !== 'article') {
        breadcrumbItems.push({
          exact: true,
          text: meta.topic.name,
          to: {
            name: 'group',
            params: { id: meta.group.id },
            query: { topic_id: meta.topic.id },
          },
          disabled: false,
        })
      } else {
        breadcrumbItems.push({
          exact: true,
          text: meta.topic.name,
          to: {
            name: 'category',
            params: { id: meta.topic.id },
          },
          disabled: false,
        })
      }
    }
    this.breadcrumbItems = breadcrumbItems

    const news = this.news
    this.attachments = news.images && news.images.teaser
      ? [news.images.teaser]
      : news.attachments

    if (!Object.prototype.hasOwnProperty.call(news, 'likes_allowed') ||
        news.likes_allowed) {
      this.fetchLikes()
    }
    if (news.comments_allowed) {
      this.fetchComments()
    }
    this.clampLines = this.getLineClampLines
  },
  mounted() {
    const el = this.$refs.post_text
    if (el) {
      this.isClamped = this.initialClamped = el.scrollHeight > el.clientHeight
    }
    this.onChangeSlide(0)
  },
  methods: {
    ...mapActions('posts', [
      'deletePost',
      'deleteComment',
    ]),
    onChangeSlide(index = 0) {
      const slide = this.attachments[index]
      if (!slide) {
        this.imageSource = null
        this.luma = 1
        return
      }
      const link = this.getLink(slide)
      const isImage = this.isImage(slide)
      if (isImage) {
        this.imageSource = new URL(link, document.baseURI).href
      } else {
        this.imageSource = null
        this.luma = 1
      }
    },
    isImage(attachment) {
      return (
        (
          isObjectLike(attachment) && attachment.type.indexOf('image') > -1
        ) ||
        (
          !isObjectLike(attachment) &&
          attachment.match(/\.(jpeg|jpg|gif|png)$/) != null
        )
      )
    },
    getLink(attachment) {
      return isObjectLike(attachment) && attachment.link
        ? attachment.link
        : attachment
    },
    onLumaCalculated(value) {
      this.luma = value
    },
    closeOverlay() {
      this.selectedOverlay = null
    },
    async fetchComments() {
      api.items.getItems('comments', { type: 'news', id: this.news.id })
        .then(({ data }) => {
          this.comments.data = data
        })
    },
    async fetchLikes() {
      api.items.getItems('likes', { type: 'news', id: this.news.id })
        .then(({ data }) => {
          this.likes.data = data
        })
    },
    onLike(val) {
      api.items.editItem(
        'likes', { type: 'news', id: this.news.id, like: val })
        .then(({ data }) => {
          this.likes.data = data
        })
    },
    onComment(val) {
      api.items.editItem(
        'comments', { type: 'news', id: this.news.id, like: val })
        .then(({ data }) => {
          this.comments.data = data
        })
    },
    fixEditorContentForLineClamp,
    getSocialMediaIconForSource,
    onDeletePost() {
      this.deletePost(this.news.id)
        .then(_ => {
          this.statusMessage =
            this.$t(this.successStatusTranslation('post', 'delete'))
          this.isStatusVisible = true
          this.closeOverlay()
        })
    },
    toggleExpand() {
      this.clampLines = this.getLineClampLines
      this.isClamped = !this.isClamped
    },
    onClickAttachmentItem(index) {
      this.overlayIndex = index
    },
    onDeleteComment(selectedCommentId, postId) {
      this.deleteComment(selectedCommentId).then(_ => {
        api.items.getItems('comments', { type: 'news', id: postId })
          .then(({ data }) => {
            this.statusMessage =
              this.$t(this.successStatusTranslation('cta_comment', 'delete'))
            this.isStatusVisible = true
            this.comments.data = data
          })
      })
    },
    clickActions(event) {
      if (this.news.meta.type === 'article') {
        this.$router.push({ name: 'page', params: { id: this.news.id }})
      } else {
        const target = event.target
        this.selectedOverlay = 'newsItemOverlay'
        const isLike = target.parentNode.classList.contains('like-count')
        const isComment =
          target.parentNode.classList.contains('comment-count') ||
          target.classList.contains('m1-icon-comment')
        this.selectedArea = isLike ? 'likes' : isComment ? 'comments' : null
      }
    },
  },
}
</script>
